import { useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { api } from '../../api/client'
import { toast } from 'sonner'

interface ProfileStepProps {
    onNext: () => void
    onSkip: (step: number) => void
}

const BROKERAGES = [
    'Fidelity',
    'Lively',
    'HealthEquity',
    'Optum Bank',
    'HSA Bank',
    'TD Ameritrade',
    'Schwab',
    'Other',
]

const CURRENT_YEAR = new Date().getFullYear()
const TARGET_YEARS = ['Not sure yet', ...Array.from({ length: 35 }, (_, i) => String(CURRENT_YEAR + i + 1))]

export default function ProfileStep({ onNext, onSkip }: ProfileStepProps) {
    const posthog = usePostHog()

    // Section A — About You (Optional)
    const [coverageType, setCoverageType] = useState<'Individual' | 'Family'>('Individual')
    const [targetYear, setTargetYear] = useState('')
    const [age, setAge] = useState('')
    const [balanceTarget, setBalanceTarget] = useState('')

    // Section B — HSA Account (Required)
    const [brokerage, setBrokerage] = useState('')
    const [otherBrokerage, setOtherBrokerage] = useState('')
    const [nickname, setNickname] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const effectiveBrokerage = brokerage === 'Other' ? otherBrokerage : brokerage

    // Pre-fill nickname when brokerage changes
    const handleBrokerageChange = (val: string) => {
        setBrokerage(val)
        if (val && val !== 'Other') {
            setNickname(`${val} HSA`)
        } else {
            setNickname('')
        }
    }

    const isValid = coverageType && brokerage && (brokerage !== 'Other' || otherBrokerage.trim())

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!isValid) return
        setIsSubmitting(true)
        try {
            // 1. Update UserProfile
            const calculatedBirthYear = age ? CURRENT_YEAR - parseInt(age, 10) : undefined
            const parsedTarget = balanceTarget ? parseFloat(balanceTarget.replace(/[^0-9.]/g, '')) : undefined

            await api.updateUserProfile({
                coverageType,
                targetReimbursementYear: targetYear && targetYear !== 'Not sure yet' ? parseInt(targetYear, 10) : undefined,
                birthYear: calculatedBirthYear,
                stealthIraTarget: parsedTarget && !isNaN(parsedTarget) ? parsedTarget : undefined,
            })

            // 2. Create HSA Account
            await api.createHsaAccount({
                name: nickname.trim() || `${effectiveBrokerage} HSA`,
                custodian: effectiveBrokerage,
                owner: 'Self',
                status: 'Active',
            })

            posthog.capture('onboarding_profile_completed', {
                coverageType,
                targetYear,
                brokerage: effectiveBrokerage,
            })

            onNext()
        } catch {
            toast.error('Something went wrong. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="profile-step">
            <div className="wizard-header">
                <span className="wizard-step-label">Step 2 of 3</span>
                <h2 className="wizard-title">Tell us about yourself</h2>
                <p className="wizard-subtitle">We'll personalize your experience based on your answers.</p>
            </div>

            <form onSubmit={handleSubmit} className="profile-form" id="profile-step-form">

                {/* Section A: About You */}
                <section className="form-section">
                    <h3 className="form-section-title">About You</h3>
                    <p className="form-section-subtitle text-sm text-[#718096] mb-4">
                        These fields are optional and are used to help generate personalized projections.
                    </p>

                    {/* Coverage Type */}
                    <div className="form-group">
                        <label className="form-label">Coverage Type <span className="required">*</span></label>
                        <div className="toggle-group" role="group" aria-label="Coverage type">
                            {(['Individual', 'Family'] as const).map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`toggle-btn${coverageType === type ? ' active' : ''}`}
                                    onClick={() => setCoverageType(type)}
                                    id={`coverage-${type.toLowerCase()}`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Age (optional) */}
                    <div className="form-group">
                        <label htmlFor="age" className="form-label">
                            Age <span className="form-hint">(Optional — helps us remind you about catch-up contributions at 55+)</span>
                        </label>
                        <input
                            id="age"
                            type="number"
                            min="18"
                            max="120"
                            className="form-input"
                            value={age}
                            onChange={(e) => setAge(e.target.value)}
                            placeholder="e.g. 35"
                        />
                    </div>

                    {/* Target Reimbursement Year */}
                    <div className="form-group">
                        <label htmlFor="target-year" className="form-label">
                            Target Reimbursement Year <span className="form-hint">(Optional — used to project account growth)</span>
                        </label>
                        <select
                            id="target-year"
                            className="form-select"
                            value={targetYear}
                            onChange={(e) => setTargetYear(e.target.value)}
                        >
                            <option value="">Select a year...</option>
                            {TARGET_YEARS.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Balance Target */}
                    <div className="form-group">
                        <label htmlFor="balance-target" className="form-label">
                            Target HSA Balance <span className="form-hint">(Optional — the ultimate goal for your HSA)</span>
                        </label>
                        <input
                            id="balance-target"
                            type="text"
                            inputMode="decimal"
                            className="form-input"
                            value={balanceTarget}
                            onChange={(e) => setBalanceTarget(e.target.value)}
                            placeholder="e.g. 100000"
                        />
                    </div>
                </section>

                {/* Section B: Your HSA Account */}
                <section className="form-section">
                    <h3 className="form-section-title">Your HSA Account</h3>

                    {/* Brokerage */}
                    <div className="form-group">
                        <label htmlFor="brokerage" className="form-label">
                            HSA Brokerage <span className="required">*</span>
                        </label>
                        <select
                            id="brokerage"
                            className="form-select"
                            value={brokerage}
                            onChange={(e) => handleBrokerageChange(e.target.value)}
                            required
                        >
                            <option value="">Select your brokerage...</option>
                            {BROKERAGES.map((b) => (
                                <option key={b} value={b}>{b}</option>
                            ))}
                        </select>
                    </div>

                    {/* Other brokerage text input */}
                    {brokerage === 'Other' && (
                        <div className="form-group">
                            <label htmlFor="other-brokerage" className="form-label">
                                Brokerage Name <span className="required">*</span>
                            </label>
                            <input
                                id="other-brokerage"
                                type="text"
                                className="form-input"
                                value={otherBrokerage}
                                onChange={(e) => setOtherBrokerage(e.target.value)}
                                placeholder="e.g. WealthCare Saver"
                                required
                            />
                        </div>
                    )}

                    {/* Nickname */}
                    <div className="form-group">
                        <label htmlFor="account-nickname" className="form-label">
                            Account Nickname <span className="form-hint">(Optional)</span>
                        </label>
                        <input
                            id="account-nickname"
                            type="text"
                            className="form-input"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            placeholder={effectiveBrokerage ? `${effectiveBrokerage} HSA` : 'e.g. Fidelity — Main'}
                        />
                    </div>

                    <p className="form-footnote">
                        You can add additional HSA accounts anytime from the{' '}
                        <a href="/accounts" style={{ color: 'var(--color-gold)' }}>Accounts page</a>.
                    </p>
                </section>

                <div className="wizard-actions">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!isValid || isSubmitting}
                        id="profile-step-submit"
                    >
                        {isSubmitting ? 'Saving…' : 'Continue →'}
                    </button>
                    <button
                        type="button"
                        className="btn-link skip-link"
                        onClick={() => onSkip(2)}
                        id="profile-step-skip"
                    >
                        Skip setup
                    </button>
                </div>
            </form>
        </div>
    )
}
